/**
 * Hook to provide folder item counts using the linkEngine
 * 
 * This hook subscribes to the linkEngine and provides real-time item counts
 * for folders based on the current game and item type.
 */

import { useState, useEffect, useMemo } from 'react';
import { subscribeLinkEngine, getAllFolderCounts, LinkEngineState } from '@/rerun-manager/linkEngine';
import { FolderItem } from '@/components/layout/side-bars/main-sidebar/organization/FolderStructure';

export interface UseFolderItemCountsProps {
  gameId?: string;
  itemType: 'glossary' | 'document' | 'blueprint' | 'dialog' | 'flow';
  folders: FolderItem[];
}

export interface UseFolderItemCountsReturn {
  foldersWithCounts: FolderItem[];
  isLoading: boolean;
  totalItemCount: number;
  rootItemCount: number;
}

/**
 * Hook that enhances folder data with real-time item counts from linkEngine
 */
export function useFolderItemCounts({
  gameId,
  itemType,
  folders,
}: UseFolderItemCountsProps): UseFolderItemCountsReturn {
  const [linkEngineState, setLinkEngineState] = useState<LinkEngineState | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Subscribe to linkEngine state changes
  useEffect(() => {
    const unsubscribe = subscribeLinkEngine((state) => {
      setLinkEngineState(state);
      setIsLoading(!state.isInitialized);
    });

    return unsubscribe;
  }, []);

  // Calculate folder counts and enhance folder data
  const { foldersWithCounts, totalItemCount, rootItemCount } = useMemo(() => {
    if (!gameId || !linkEngineState?.isInitialized) {
      return {
        foldersWithCounts: folders,
        totalItemCount: 0,
        rootItemCount: 0,
      };
    }

    // Get all folder counts for this game and item type
    const folderCounts = getAllFolderCounts(gameId, itemType);
    
    // Calculate total and root counts
    let total = 0;
    let rootCount = 0;
    
    for (const [folderId, count] of folderCounts.entries()) {
      total += count;
      if (folderId === 'root') {
        rootCount = count;
      }
    }

    // Recursively enhance folders with item counts
    const enhanceFoldersWithCounts = (folderList: FolderItem[]): FolderItem[] => {
      return folderList.map(folder => {
        const itemCount = folderCounts.get(folder.id) || 0;
        
        // Recursively process children
        const enhancedChildren = folder.children 
          ? enhanceFoldersWithCounts(folder.children)
          : undefined;

        return {
          ...folder,
          itemCount,
          children: enhancedChildren,
        };
      });
    };

    const enhancedFolders = enhanceFoldersWithCounts(folders);

    return {
      foldersWithCounts: enhancedFolders,
      totalItemCount: total,
      rootItemCount: rootCount, // Use the local variable name
    };
  }, [gameId, itemType, folders, linkEngineState]);

  return {
    foldersWithCounts,
    isLoading,
    totalItemCount,
    rootItemCount,
  };
}

/**
 * Simple hook to get item count for a specific folder
 */
export function useFolderItemCount(
  gameId: string | undefined,
  folderId: string | null,
  itemType: 'glossary' | 'document' | 'blueprint' | 'dialog' | 'flow'
): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!gameId) {
      setCount(0);
      return;
    }

    const unsubscribe = subscribeLinkEngine((state) => {
      if (state.isInitialized) {
        const folderCounts = getAllFolderCounts(gameId, itemType);
        const folderKey = folderId || 'root';
        setCount(folderCounts.get(folderKey) || 0);
      }
    });

    return unsubscribe;
  }, [gameId, folderId, itemType]);

  return count;
}
